export interface ChapterConfig {
  label: string;
  color: string;           // CSS color for glow + active state
  sceneIndices: number[];  // flat scene indices belonging to this chapter
}

export class NavBar {
  private readonly el: HTMLElement;
  private _current = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly chapters: ChapterConfig[],
    private readonly sceneLabels: string[],
    private readonly onChange: (index: number) => void,
  ) {
    this.el = this.buildDOM();
    root.appendChild(this.el);

    // Chapter button clicks → jump to first scene in that chapter
    this.el.querySelectorAll<HTMLButtonElement>('.sm-chapter-btn').forEach((btn, ci) => {
      btn.addEventListener('click', () => {
        const first = this.chapters[ci].sceneIndices[0];
        this.onChange(first);
      });
    });

    this.el.querySelector('#sm-prev')!.addEventListener('click', () => this.onChange(this._current - 1));
    this.el.querySelector('#sm-next')!.addEventListener('click', () => this.onChange(this._current + 1));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.onChange(this._current + 1);
      if (e.key === 'ArrowLeft')  this.onChange(this._current - 1);
    });

    this.update(0);
  }

  update(index: number): void {
    this._current = index;
    const totalScenes = this.sceneLabels.length;

    // Update chapter buttons — highlight the active chapter
    this.el.querySelectorAll<HTMLButtonElement>('.sm-chapter-btn').forEach((btn, ci) => {
      const isActive = this.chapters[ci].sceneIndices.includes(index);
      btn.classList.toggle('active', isActive);
    });

    // Rebuild scene dots for the active chapter only
    const activeChapter = this.chapters.find(ch => ch.sceneIndices.includes(index))
      ?? this.chapters[0];
    const dotRow = this.el.querySelector<HTMLElement>('#sm-dot-row')!;
    dotRow.innerHTML = activeChapter.sceneIndices.map(si => {
      const active = si === index;
      return `<button class="sm-navdot${active ? ' active' : ''}" data-scene="${si}"
        title="${this.sceneLabels[si] ?? ''}"
        style="--ch-color:${activeChapter.color}"></button>`;
    }).join('');
    dotRow.querySelectorAll<HTMLButtonElement>('.sm-navdot').forEach(dot => {
      const si = Number(dot.dataset['scene']);
      dot.addEventListener('click', () => this.onChange(si));
    });

    // Scene label
    const labelEl = this.el.querySelector<HTMLElement>('.sm-scene-label');
    if (labelEl) labelEl.textContent = this.sceneLabels[index] ?? '';

    // Prev / next arrows
    const prev = this.el.querySelector<HTMLButtonElement>('#sm-prev')!;
    const next = this.el.querySelector<HTMLButtonElement>('#sm-next')!;
    prev.disabled = index === 0;
    next.disabled = index === totalScenes - 1;
  }

  private buildDOM(): HTMLElement {
    const nav = document.createElement('nav');
    nav.id = 'sm-nav';

    const chapterItems = this.chapters.map((ch, i) => {
      const connector = i < this.chapters.length - 1
        ? '<div class="sm-chapter-connector"></div>'
        : '';
      return `
        <button class="sm-chapter-btn" style="--ch-color:${ch.color}">
          <div class="sm-chapter-circle"></div>
          <span class="sm-chapter-label">${ch.label}</span>
        </button>
        ${connector}`;
    }).join('');

    nav.innerHTML = `
      <div id="sm-chapter-row">${chapterItems}</div>
      <div id="sm-scene-row">
        <button class="sm-nav-arrow" id="sm-prev">&#8249;</button>
        <div id="sm-dot-row"></div>
        <button class="sm-nav-arrow" id="sm-next">&#8250;</button>
      </div>
      <span class="sm-scene-label"></span>
    `;
    return nav;
  }
}
